import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/ClassRoom.css';
import LanguageSettingsModal from '../components/LanguageSettingsModal';
import MicrophoneSettingsModal from '../components/MicrophoneSettingsModal';
import LayoutSettingsModal from '../components/LayoutSettingsModal';
import TtsSettingsModal from '../components/TtsSettingsModal';
import QuestionModal from '../components/QuestionModal';
import QuestionsListModal from '../components/QuestionsListModal';
import { ClassData, ConnectionState, getErrorMessage } from '../types';
import { findLanguageByCode } from '../constants/letureLanguages';
import { useAuth } from '../contexts/AuthContext';
import io from 'socket.io-client';
import api from '../utils/api';
import audioQueueManager from '../utils/audioQueue';
import QRCode from 'qrcode';
import InputLanguageSettingsModal from '../components/InputLanguageSettingModal';

// 반응형 유틸리티 함수
const getScreenSize = () => {
    const width = window.innerWidth;
    if (width <= 480) return 'mobile';
    if (width <= 768) return 'tablet';
    if (width <= 1024) return 'desktop-small';
    return 'desktop';
};

// 반응형 스타일을 위한 훅
const useResponsiveStyles = () => {
    const [screenSize, setScreenSize] = useState(getScreenSize());

    useEffect(() => {
        const handleResize = () => {
            setScreenSize(getScreenSize());
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return screenSize;
};

// 반응형 CSS 추가
const getResponsiveTranslationStyles = (screenSize: string) => ({
    translationItem: {
        padding: screenSize === 'mobile' ? '12px' : '10px',
        borderRadius: screenSize === 'mobile' ? '6px' : '4px',
        backgroundColor: '#f9f9f9',
        marginBottom: screenSize === 'mobile' ? '10px' : '8px',
    },
    originalText: {
        fontWeight: 'bold',
        margin: '0 0 8px 0',
        lineHeight: screenSize === 'mobile' ? '1.6' : '1.4',
    },
    translatedText: {
        margin: '0',
        paddingLeft: screenSize === 'mobile' ? '12px' : '15px',
        borderLeft: '2px solid #ddd',
        lineHeight: screenSize === 'mobile' ? '1.6' : '1.4',
    },
    languageSelector: {
        padding: screenSize === 'mobile' ? '8px 12px' : '6px 10px',
        border: '1px solid #d1d5db',
        borderRadius: screenSize === 'mobile' ? '8px' : '6px',
        fontSize: screenSize === 'mobile' ? '14px' : '14px',
        cursor: 'pointer',
        backgroundColor: '#ffffff',
        color: '#374151',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.15s ease-in-out',
        outline: 'none',
        fontWeight: '500',
        minWidth: screenSize === 'mobile' ? '120px' : 'auto',
        minHeight: screenSize === 'mobile' ? '44px' : 'auto',
    },
    translationsPanel: {
        width: '100%',
        height: screenSize === 'mobile' ? '500px' : '100%',
        display: 'flex',
        flexDirection: 'column' as const,
        backgroundColor: 'white',
        borderRadius: screenSize === 'mobile' ? '10px' : '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        padding: screenSize === 'mobile' ? '16px' : '15px',
        flex: '1',
    },
    panelHeader: {
        borderBottom: '1px solid #eee',
        paddingBottom: screenSize === 'mobile' ? '15px' : '10px',
        marginBottom: screenSize === 'mobile' ? '15px' : '10px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'nowrap' as const,
        gap: screenSize === 'mobile' ? '12px' : '10px',
    },
    panelContent: {
        overflow: 'auto',
        flex: '1',
        padding: screenSize === 'mobile' ? '10px 0' : '10px 5px',
        height: screenSize === 'mobile' ? '400px' : '100%',
        maxHeight: screenSize === 'mobile' ? '400px' : 'none',
    },
});

// 메인 레이아웃 스타일 반응형 수정
const getResponsiveLayoutStyles = (screenSize: string, showClassMaterials: boolean) => ({
    classroomMain: {
        display: 'flex',
        flexDirection: 'column' as const,
        height: screenSize === 'mobile' ? 'auto' : '100vh',
        overflow: screenSize === 'mobile' ? 'auto' : 'hidden',
    },
    classroomContent: {
        display: 'flex',
        flexDirection: (screenSize === 'mobile' ? 'column' : 'row') as 'column' | 'row',
        gap: screenSize === 'mobile' ? '10px' : '20px',
        flex: '1',
        overflow: screenSize === 'mobile' ? 'visible' : 'hidden',
        width: screenSize === 'mobile' ? '100%' : '100%',
        height: screenSize === 'mobile' ? 'auto' : '100%',
    },
    fileViewerPanel: {
        flex: screenSize === 'mobile' ? 'none' : '1',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column' as const,
        overflow: 'hidden',
        width: screenSize === 'mobile' ? '100%' : '50%',
        height: screenSize === 'mobile' ? '350px' : '100%',
        maxWidth: screenSize === 'mobile' ? '100%' : '50%',
    },
    translationSection: {
        flex: screenSize === 'mobile' ? 'none' : '1',
        display: 'flex',
        flexDirection: 'column' as const,
        overflow: 'hidden',
        width: screenSize === 'mobile' ? '100%' : showClassMaterials ? '50%' : '100%',
        height: screenSize === 'mobile' ? '500px' : '100%',
        maxWidth: screenSize === 'mobile' ? '100%' : showClassMaterials ? '50%' : '100%',
    },
    allTranslationsContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        flex: '1',
        height: '100%',
        overflow: 'auto',
        gap: screenSize === 'mobile' ? '10px' : '20px',
    },
    translationPanel: {
        flex: '1',
        minHeight: screenSize === 'mobile' ? '150px' : '200px',
        marginBottom: '10px',
        display: 'flex',
        flexDirection: 'column' as const,
    },
    translationsGrid: {
        display: screenSize === 'mobile' ? 'flex' : 'grid',
        flexDirection: screenSize === 'mobile' ? ('column' as const) : undefined,
        gridTemplateColumns:
            screenSize === 'mobile'
                ? undefined
                : screenSize === 'tablet'
                ? 'repeat(auto-fit, minmax(280px, 1fr))'
                : 'repeat(auto-fit, minmax(300px, 1fr))',
        gridAutoRows: screenSize === 'mobile' ? undefined : 'auto',
        gap: screenSize === 'mobile' ? '8px' : '15px',
        width: '100%',
        height: screenSize === 'mobile' ? '500px' : '100%',
        overflow: 'auto' as const,
        padding: screenSize === 'mobile' ? '5px' : '10px',
        boxSizing: 'border-box' as const,
        flex: '1',
    },
    gridPanel: {
        height: screenSize === 'mobile' ? '400px' : 'auto',
        minHeight: screenSize === 'mobile' ? '400px' : '250px',
        width: screenSize === 'mobile' ? '100%' : 'auto',
        display: 'flex',
        flexDirection: 'column' as const,
        backgroundColor: 'white',
        borderRadius: screenSize === 'mobile' ? '10px' : '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        flex: '1',
        padding: screenSize === 'mobile' ? '16px' : '15px',
    },
});

// 번역 결과 인터페이스 정의
interface TranslationResult {
    results: Array<{
        id: string;
        sourceText: string;
        sourceLanguage: string;
        translations: Array<{
            text: string;
            language: string;
        }>;
        timestamp: number;
    }>;
}

// PDF 문서 인터페이스 추가
interface PdfDoc {
    id: string;
    filename: string;
    url: string;
    uploaderId: string;
    uploaderName: string;
    uploadedAt: string;
}

// 강의 레이아웃을 위한 ClassRoom 컴포넌트
const ClassRoom = () => {
    const { classId } = useParams<{ classId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const userId = user?.id || '';
    const userName = user?.username || '';
    const isProfessor = user?.role === 'PROFESSOR';
    const screenSize = useResponsiveStyles();

    const [classData, setClassData] = useState<ClassData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedLanguage, setSelectedLanguage] = useState<string>('');
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [showLanguageModal, setShowLanguageModal] = useState(false);
    const [showInputLanguageModal, setShowInputLanguageModal] = useState(false);
    const [showMicrophoneModal, setShowMicrophoneModal] = useState(false);
    const [showLayoutModal, setShowLayoutModal] = useState(false);
    const [showTtsModal, setShowTtsModal] = useState(false);
    const [ttsEnabled, setTtsEnabled] = useState(false);
    const [translations, setTranslations] = useState<any[]>([]);
    const [lastProcessedTranslationId, setLastProcessedTranslationId] = useState<string | null>(
        null,
    );
    const ttsEnabledRef = useRef(false);
    const [currentTranscription, setCurrentTranscription] = useState<{
        id: string;
        text: string;
        isFinal: boolean;
        translations: any;
        lastUpdated: number;
        isProcessing: boolean;
    } | null>(null);
    const [fontSize, setFontSize] = useState<number>(16);
    const [fileUrl, setFileUrl] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [inputLanguage, setInputLanguage] = useState<string>('');
    const [targetLanguages, setTargetLanguages] = useState<string[]>([]);
    const [selectedMicrophoneId, setSelectedMicrophoneId] = useState<string>('');
    const [isClassStarted, setIsClassStarted] = useState(false);
    const [documentViewerUrl, setDocumentViewerUrl] = useState<string | null>(null);
    const [forceUpdate, setForceUpdate] = useState(0);
    const [connectionState, setConnectionState] = useState<ConnectionState>({
        isDeepgramReady: false,
        isStreamActive: false,
    });
    const [socket, setSocket] = useState<any>(null);
    const [showClassMaterials, setShowClassMaterials] = useState(true);
    const [showAllLanguages, setShowAllLanguages] = useState(false);
    const [showQuestionModal, setShowQuestionModal] = useState(false);
    const [showQuestionsListModal, setShowQuestionsListModal] = useState(false);
    const [hasNewQuestions, setHasNewQuestions] = useState(false);
    const [showQrModal, setShowQrModal] = useState(false);
    const [qrCodeUrl, setQrCodeUrl] = useState<string>('');

    // 반응형 스타일 객체 생성
    const translationStyles = getResponsiveTranslationStyles(screenSize);
    const layoutStyles = getResponsiveLayoutStyles(screenSize, showClassMaterials);

    // 마이크 테스트 관련 상태
    const audioContextRef = useRef<AudioContext | null>(null);
    const audioStreamRef = useRef<MediaStream | null>(null);
    const workletNodeRef = useRef<AudioWorkletNode | null>(null);

    // 오디오 로깅 관련 상태
    const lastAudioLogTimeRef = useRef<number>(0);
    const lastAudioSentLogTimeRef = useRef<number>(0);

    const translationsPanelRef = useRef<HTMLDivElement>(null);
    const mainPanelRef = useRef<HTMLDivElement>(null);
    const languageRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

    // 번역 활성화 상태 추가
    const [isTranslationActive, setIsTranslationActive] = useState(false);

    // PDF 관련 상태 추가
    const [pdfList, setPdfList] = useState<PdfDoc[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // 글꼴 크기 옵션 배열
    const fontSizeOptions = [10, 12, 14, 16, 18, 20, 22, 24, 28, 32, 36, 40, 44, 48];

    // 글꼴 크기 변경 함수
    const changeFontSize = (size: number) => {
        setFontSize(size);
        localStorage.setItem('translationFontSize', size.toString());
    };

    // targetLanguages가 로드된 후 첫 번째 유효한 언어 선택
    useEffect(() => {
        if (targetLanguages?.length > 0 && !selectedLanguage) {
            // 한국어를 제외한 첫 번째 언어 선택
            const firstNonKoreanLang = targetLanguages.find(lang => lang !== 'ko-KR');
            if (firstNonKoreanLang) {
                setSelectedLanguage(firstNonKoreanLang);
            }
        }
    }, [targetLanguages, selectedLanguage]);

    // 자동 스크롤 함수
    const scrollToBottom = (element: HTMLDivElement | null) => {
        if (element) {
            // DOM 업데이트 후 스크롤 실행
            setTimeout(() => {
                element.scrollTop = element.scrollHeight;
            }, 10);
        }
    };

    // 새 번역 결과가 추가되면 자동 스크롤
    useEffect(() => {
        // DOM 업데이트 후 스크롤 실행
        setTimeout(() => {
            // 단일 언어 모드에서는 메인 패널 스크롤
            if (!showAllLanguages) {
                scrollToBottom(translationsPanelRef.current);
            } else {
                // 모든 언어 모드에서는 각 언어 패널 스크롤
                Object.values(languageRefs.current).forEach(element => {
                    scrollToBottom(element);
                });
            }
        }, 100);
    }, [translations, showAllLanguages, currentTranscription]);

    // 일정 시간 이후 자동으로 currentTranscription 초기화
    useEffect(() => {
        if (currentTranscription && !currentTranscription.isFinal) {
            // 음성 인식 결과가 5초 동안 업데이트되지 않으면 자동으로 초기화
            const timer = setTimeout(() => {
                const now = Date.now();
                const lastUpdate = currentTranscription.lastUpdated;

                // 마지막 업데이트 후 5초가 지났으면 초기화
                if (now - lastUpdate > 5000) {
                    setCurrentTranscription(null);
                }
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [currentTranscription]);

    // 번역 결과 처리 함수
    const handleTranslationResult = useCallback((data: TranslationResult) => {
        // 번역 결과가 없거나 유효하지 않은 경우 무시
        if (!data || !data.results || !Array.isArray(data.results) || data.results.length === 0) {
            console.warn('유효하지 않은 번역 결과 형식:', data);
            return;
        }

        // 번역 결과를 누적하여 저장 (이전 결과 유지)
        setTranslations(prevTranslations => {
            // 새로운 결과와 이전 결과 병합
            const newResults = [...prevTranslations];

            // 각 새 결과에 대해 처리
            data.results.forEach(result => {
                // 같은 ID나 같은 원본 텍스트가 있는지 확인
                const existingIndex = newResults.findIndex(
                    t =>
                        t.id === result.id ||
                        (t.sourceText &&
                            result.sourceText &&
                            t.sourceText.trim().toLowerCase() ===
                                result.sourceText.trim().toLowerCase()),
                );

                let isNewResult = false;
                if (existingIndex >= 0) {
                    // 이미 있는 결과는 업데이트
                    newResults[existingIndex] = result;
                } else {
                    // 새 결과는 추가
                    newResults.push(result);
                    isNewResult = true;
                }
            });

            return newResults;
        });
    }, []);

    // 컴포넌트 마운트 - 강의 데이터 로드
    useEffect(() => {
        const fetchClassData = async () => {
            try {
                setIsLoading(true);
                setError(null);
                let response;
                if (!user) {
                    response = await api.get(`/classes/public/${classId}`);
                } else {
                    response = await api.get(`/classes/${classId}`);
                }

                setClassData(response.data.result);

                // targetLanguages 설정
                if (response.data.result.targetLanguages) {
                    setTargetLanguages(response.data.result.targetLanguages);
                }

                if (response.data.result.inputLanguage) {
                    setInputLanguage(response.data.result.inputLanguage);
                }

                // 권한 체크 - 학생 대시보드에서 이미 비밀번호 검증을 했으므로 여기서는 검증하지 않음
                // 교수는 비밀번호 없이 바로 입장 가능
                // 소켓 재연결 로직 만들기!!!!!!1
                if (isProfessor || user?.role === 'PROFESSOR') {
                    // 이미 진행 중인 강의인 경우 처리
                    if (response.data.result.status === 'IN_PROGRESS') {
                        setIsClassStarted(true);
                    }
                }

                // 교수가 아닌 경우에는 joinClass 함수에서 처리
                // joinClass();

                // 이미 진행 중인 강의인 경우 처리
                if (response.data.result.status === 'IN_PROGRESS') {
                    setIsClassStarted(true);

                    // 소켓 재연결
                    await reconnect();

                    // 진행 중인 강의라면 기존 번역 기록 복원
                    await fetchExistingTranslations();
                }
            } catch (err) {
                setError(`강의 정보를 불러오는 중 오류가 발생했습니다: ${getErrorMessage(err)}`);
            } finally {
                setIsLoading(false);
            }
        };

        const reconnect = async () => {
            try {
                const response = await socket.emit('reconnect', {
                    classId: classData?.id,
                    className: classData?.title,
                    professorId: classData?.professorId,
                    professorLanguage: classData?.inputLanguage,
                    targetLanguages: classData?.targetLanguages,
                });
            } catch (err) {}
        };

        // 기존 번역 기록을 가져오는 함수
        const fetchExistingTranslations = async () => {
            try {
                // Redis에서 현재 진행 중인 번역 기록 가져오기
                let translationsResponse;
                if (!user) {
                    translationsResponse = await api.get(
                        `/classes/public/${classId}/translations/current`,
                    );
                } else {
                    translationsResponse = await api.get(
                        `/classes/${classId}/translations/current`,
                    );
                }

                if (
                    translationsResponse.data.result &&
                    Array.isArray(translationsResponse.data.result)
                ) {
                    const existingTranslations = translationsResponse.data.result;

                    // 번역 결과를 적절한 형식으로 변환
                    const formattedTranslations = existingTranslations.map(
                        (item: any, index: number) => {
                            // translations 필드 처리
                            let translationsArray = [];

                            if (item.translations) {
                                if (Array.isArray(item.translations)) {
                                    // 이미 배열 형태인 경우
                                    translationsArray = item.translations;
                                } else if (
                                    typeof item.translations === 'object' &&
                                    item.translations !== null
                                ) {
                                    // 객체 형태인 경우 배열로 변환
                                    translationsArray = Object.entries(item.translations).map(
                                        ([language, text]) => ({
                                            language,
                                            text: text as string,
                                        }),
                                    );
                                } else if (typeof item.translations === 'string') {
                                    // 문자열인 경우 - 기본 언어로 처리
                                    // 첫 번째 타겟 언어로 설정 (한국어 제외)
                                    const firstTargetLang =
                                        targetLanguages?.find(lang => lang !== 'ko-KR') || 'en';
                                    translationsArray = [
                                        {
                                            language: firstTargetLang,
                                            text: item.translations as string,
                                        },
                                    ];
                                }
                            }

                            return {
                                id: item.id || `existing-${index}`,
                                sourceText: item.originalText || item.sourceText,
                                sourceLanguage:
                                    item.originalLanguage || item.sourceLanguage || 'ko',
                                translations: translationsArray,
                                timestamp: new Date(item.timestamp).getTime(),
                            };
                        },
                    );

                    // 기존 번역 기록을 상태에 설정
                    if (formattedTranslations.length > 0) {
                        const translationResult: TranslationResult = {
                            results: formattedTranslations,
                        };
                        handleTranslationResult(translationResult);
                    }
                }
            } catch (error) {
                console.warn('기존 번역 기록 가져오기 실패:', error);
            }
        };

        if (classId) {
            fetchClassData();

            // 저장된 마이크 ID 로드
            const savedMicrophoneId = localStorage.getItem('selectedMicrophoneId');
            if (savedMicrophoneId) {
                setSelectedMicrophoneId(savedMicrophoneId);
            }

            // 저장된 레이아웃 설정 로드
            const savedShowClassMaterials = localStorage.getItem('showClassMaterials');
            const savedShowAllLanguages = localStorage.getItem('showAllLanguages');

            if (savedShowClassMaterials !== null) {
                setShowClassMaterials(savedShowClassMaterials === 'true');
            }

            if (savedShowAllLanguages !== null) {
                setShowAllLanguages(savedShowAllLanguages === 'true');
            }

            // 저장된 글꼴 크기 설정 로드
            const savedFontSize = localStorage.getItem('translationFontSize');
            if (savedFontSize) {
                const parsedSize = parseInt(savedFontSize, 10);
                if (!isNaN(parsedSize) && fontSizeOptions.includes(parsedSize)) {
                    setFontSize(parsedSize);
                }
            }

            // TTS는 기본적으로 비활성화 상태로 시작
            setTtsEnabled(false);
        }
    }, [classId, navigate, userId, isProfessor, user?.role]);

    // TTS 상태 변경 감지 - ref 업데이트 및 비활성화 시 즉시 오디오 중단
    useEffect(() => {
        ttsEnabledRef.current = ttsEnabled;

        if (!ttsEnabled) {
            // TTS가 비활성화되면 즉시 오디오 중단 및 큐 클리어
            audioQueueManager.clear();
        }
    }, [ttsEnabled]);

    // 번역 결과 변경 시 TTS 처리
    useEffect(() => {
        // TTS가 활성화되어 있고, 학생이며, 번역 결과가 있을 때만 처리
        if (ttsEnabledRef.current && !isProfessor && translations.length > 0) {
            // 가장 최근 번역 결과 가져오기
            const latestTranslation = translations[translations.length - 1];

            // 이미 처리된 번역인지 확인
            if (
                latestTranslation &&
                latestTranslation.id !== lastProcessedTranslationId &&
                latestTranslation.translations
            ) {
                let englishText = '';

                if (Array.isArray(latestTranslation.translations)) {
                    // 영어 번역 찾기
                    const englishTranslation = latestTranslation.translations.find(
                        (t: any) => t.language === 'en' || t.language === 'en-US',
                    );
                    if (englishTranslation && englishTranslation.text) {
                        englishText = englishTranslation.text;
                    }
                } else if (typeof latestTranslation.translations === 'object') {
                    // 객체 형태에서 영어 번역 찾기
                    englishText =
                        latestTranslation.translations['en'] ||
                        latestTranslation.translations['en-US'] ||
                        '';
                }

                // 영어 텍스트가 있으면 TTS API 호출 후 오디오 큐에 추가
                if (englishText.trim()) {
                    audioQueueManager.enqueue(englishText, 'en');
                    // 처리된 번역 ID 기록
                    setLastProcessedTranslationId(latestTranslation.id);
                }
            }
        }
    }, [translations]); // ttsEnabled, isProfessor 제거 - translations 변경 시에만 실행

    // 소켓 연결 설정 - 소켓 객체만 생성하는 useEffect
    useEffect(() => {
        // socket.io-client로 연결 생성
        const socket = io(`${process.env.REACT_APP_SOCKET_URL}/speech`);
        setSocket(socket);

        // 클린업 함수: 컴포넌트 언마운트 시 소켓 연결 정리
        return () => {
            socket.disconnect();
        };
    }, []); // 빈 의존성 배열: 마운트 시 한 번만 실행

    // 소켓 이벤트 리스너 설정 (소켓이 변경될 때마다 업데이트)
    useEffect(() => {
        if (!socket) return;

        // 연결 이벤트
        const handleConnect = () => {};

        // 서버로부터 메시지 수신
        const handleMessage = (data: any) => {};

        // 연결 에러 처리
        const handleConnectError = (error: any) => {
            console.error('Socket.io 연결 에러:', error);
        };

        // 연결 종료 감지
        const handleDisconnect = (reason: string) => {};

        // 서버로부터 음성 인식 및 번역 결과 수신
        const handleSpeechTranslation = (data: any) => {
            // 음성 인식 결과가 있을 때만 처리
            if (data.originalText) {
                // 고유 ID 생성 - 원본 텍스트의 해시값으로 ID 생성 (같은 내용이면 같은 ID)
                const textHash = data.originalText.trim().toLowerCase();
                const transcriptionId = `speech-${textHash}`;

                // 번역 결과를 적절한 형식으로 변환
                const translationsArray =
                    data.translations && Object.keys(data.translations).length > 0
                        ? Object.entries(data.translations).map(
                              ([language, text]: [string, any]) => ({
                                  language,
                                  text: text as string,
                              }),
                          )
                        : [];

                // 최종 결과가 아닐 때 (음성 인식 중)
                if (!data.isFinal) {
                    // 기존 currentTranscription이 없거나 새로운 내용이 이전 내용을 포함하는 경우만 업데이트
                    const newText = data.originalText.trim().toLowerCase();
                    const currentText = currentTranscription?.text?.trim().toLowerCase() || '';

                    // 새 텍스트가 기존 텍스트를 포함하거나, 기존 텍스트가 없는 경우에만 업데이트
                    if (
                        !currentText ||
                        newText.includes(currentText) ||
                        currentText.includes(newText)
                    ) {
                        // 현재 진행 중인 텍스트 업데이트 (노란색 박스)
                        setCurrentTranscription({
                            id: transcriptionId,
                            text: data.originalText,
                            isFinal: false,
                            translations: data.translations || {},
                            lastUpdated: Date.now(),
                            isProcessing: true, // 번역 처리 중
                        });
                    }
                }

                // 최종 결과인 경우 (음성 인식 완료, 번역 완료)
                if (data.isFinal) {
                    // TranslationResult 형식으로 변환
                    const translationResult: TranslationResult = {
                        results: [
                            {
                                id: transcriptionId,
                                sourceText: data.originalText,
                                sourceLanguage: data.originalLanguage || 'ko',
                                translations: translationsArray,
                                timestamp: new Date().getTime(),
                            },
                        ],
                    };

                    // 번역 결과 처리 함수 호출하여 저장
                    handleTranslationResult(translationResult);

                    // 현재 진행 중인 텍스트와 관련된 경우 currentTranscription 초기화
                    if (currentTranscription) {
                        const currentText = currentTranscription.text.trim().toLowerCase();
                        const finalText = data.originalText.trim().toLowerCase();

                        // 같은 ID이거나 내용이 유사한 경우 초기화
                        if (
                            currentTranscription.id === transcriptionId ||
                            currentText === finalText ||
                            currentText.includes(finalText) ||
                            finalText.includes(currentText)
                        ) {
                            setCurrentTranscription(null);
                        }
                    }
                }
            }
        };

        // 새 질문 이벤트 수신
        const handleNewQuestion = (data: any) => {
            // 새 질문이 있음을 표시
            setHasNewQuestions(true);

            // 교수에게만 브라우저 알림 표시 (해당 기능이 지원될 경우)
            if (isProfessor) {
                try {
                    if (typeof Notification !== 'undefined') {
                        if (Notification.permission === 'granted') {
                            new Notification('새 질문이 도착했습니다', {
                                body:
                                    data.text.substring(0, 50) +
                                    (data.text.length > 50 ? '...' : ''),
                                icon: '/favicon.ico',
                            });
                        } else if (Notification.permission !== 'denied') {
                            Notification.requestPermission();
                        }
                    }
                } catch (error) {
                    console.error('알림 표시 중 오류:', error);
                }
            }
        };

        // 기존 PDF URL 목록 수신 (늦게 참여한 학생용)
        const handleExistingPdfs = (data: { pdfs: PdfDoc[] }) => {
            setPdfList(data.pdfs);

            // 가장 최근에 업로드된 PDF가 있다면 자동으로 표시
            if (data.pdfs && data.pdfs.length > 0) {
                // 가장 최근에 업로드된 PDF 찾기
                const latestPdf = [...data.pdfs].sort(
                    (a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime(),
                )[0];

                // PDF URL 설정하여 표시
                setDocumentViewerUrl(latestPdf.url);
                setFileUrl(latestPdf.url);
            }
        };

        // 새 PDF 문서 알림 수신
        const handleNewPdf = (newPdf: PdfDoc) => {
            // PDF 목록에 추가
            setPdfList(prevList => [...prevList, newPdf]);

            // 현재 PDF 뷰어에 자동으로 로드
            setDocumentViewerUrl(newPdf.url);
            setFileUrl(newPdf.url);
        };

        // 이벤트 리스너 등록
        socket.on('connect', handleConnect);
        socket.on('message', handleMessage);
        socket.on('connect_error', handleConnectError);
        socket.on('disconnect', handleDisconnect);
        socket.on('speechTranslation', handleSpeechTranslation);
        socket.on('newQuestion', handleNewQuestion);
        socket.on('existingPdfs', handleExistingPdfs);
        socket.on('newPdf', handleNewPdf);

        // 클린업 함수: useEffect 재실행 또는 컴포넌트 언마운트 시 이벤트 리스너 제거
        return () => {
            socket.off('connect', handleConnect);
            socket.off('message', handleMessage);
            socket.off('connect_error', handleConnectError);
            socket.off('disconnect', handleDisconnect);
            socket.off('speechTranslation', handleSpeechTranslation);
            socket.off('newQuestion', handleNewQuestion);
            socket.off('existingPdfs', handleExistingPdfs);
            socket.off('newPdf', handleNewPdf);
        };
    }, [socket, isProfessor]); // socket과 isProfessor가 변경될 때마다 재실행

    // 학생이 소켓 연결 후 클래스에 참여하는 로직
    useEffect(() => {
        // 소켓이 연결되어 있고, 클래스 데이터가 로드된 경우에만 실행
        if (socket && classData) {
            if (!isProfessor) {
                // 학생인 경우 joinClass 이벤트 전송
                socket.emit(
                    'joinClass',
                    {
                        classId,
                        studentId: userId,
                        studentName: userName,
                    },
                    (response: any) => {
                        if (response && response.success) {
                            // 성공적으로 참여한 후에만 PDF 요청
                            socket.emit('getPdfs', { classId }, (pdfResponse: any) => {
                                if (pdfResponse && pdfResponse.success && pdfResponse.pdfs) {
                                    setPdfList(pdfResponse.pdfs);

                                    // 최신 PDF 자동 로드 로직 수행
                                    if (pdfResponse.pdfs.length > 0) {
                                        const latestPdf = [...pdfResponse.pdfs].sort(
                                            (a, b) =>
                                                new Date(b.uploadedAt).getTime() -
                                                new Date(a.uploadedAt).getTime(),
                                        )[0];
                                        setDocumentViewerUrl(latestPdf.url);
                                        setFileUrl(latestPdf.url);
                                    }
                                }
                            });
                        } else {
                            console.error('클래스 참여 실패:', response?.message || '응답 없음');
                            alert(
                                `클래스 참여에 실패했습니다: ${response?.message || '응답 없음'}`,
                            );
                            navigate('/dashboard');
                        }
                    },
                );
            } else {
                // 교수인 경우 클래스 재연결 (새로고침 시 소켓 재연결 처리)
                if (isClassStarted || classData.status === 'IN_PROGRESS') {
                    socket.emit(
                        'reconnectClass',
                        {
                            classId: classData.id,
                            className: classData.title,
                            professorId: classData.professorId,
                            professorLanguage: classData.inputLanguage,
                            targetLanguages: classData.targetLanguages,
                        },
                        (response: any) => {
                            if (response && response.success) {
                                // PDF 목록 요청
                                socket.emit('getPdfs', { classId }, (pdfResponse: any) => {
                                    if (pdfResponse && pdfResponse.success && pdfResponse.pdfs) {
                                        setPdfList(pdfResponse.pdfs);

                                        // 최신 PDF 자동 로드
                                        if (pdfResponse.pdfs.length > 0) {
                                            const latestPdf = [...pdfResponse.pdfs].sort(
                                                (a, b) =>
                                                    new Date(b.uploadedAt).getTime() -
                                                    new Date(a.uploadedAt).getTime(),
                                            )[0];
                                            setDocumentViewerUrl(latestPdf.url);
                                            setFileUrl(latestPdf.url);
                                        }
                                    }
                                });
                            } else {
                                console.warn(
                                    '클래스 재연결 실패:',
                                    response?.message || '응답 없음',
                                );
                            }
                        },
                    );
                }
            }
        }
    }, [socket, classData, navigate]);

    // classData 상태가 변경될 때 isClassStarted 상태 업데이트
    useEffect(() => {
        if (classData) {
            setIsClassStarted(classData.status === 'IN_PROGRESS');
        }
    }, [classData]);

    // 모달 열림/닫힘 시 body 스크롤 제어
    useEffect(() => {
        const hasOpenModal =
            showHelpModal ||
            showLanguageModal ||
            showInputLanguageModal ||
            showMicrophoneModal ||
            showLayoutModal ||
            showTtsModal ||
            showQuestionModal ||
            showQuestionsListModal ||
            showQrModal;

        if (hasOpenModal) {
            // 모달이 열릴 때 현재 스크롤 위치 저장
            const scrollY = window.scrollY;
            document.body.style.top = `-${scrollY}px`;
            document.body.classList.add('modal-open');
        } else {
            // 모달이 닫힐 때 스크롤 위치 복원
            const scrollY = parseInt(document.body.style.top || '0', 10);
            document.body.classList.remove('modal-open');
            document.body.style.top = '';
            window.scrollTo(0, -scrollY);
        }

        // 컴포넌트 언마운트 시 정리
        return () => {
            document.body.classList.remove('modal-open');
            document.body.style.top = '';
        };
    }, [
        showHelpModal,
        showLanguageModal,
        showInputLanguageModal,
        showMicrophoneModal,
        showLayoutModal,
        showTtsModal,
        showQuestionModal,
        showQuestionsListModal,
        showQrModal,
    ]);

    // 연결 상태 업데이트 함수
    const updateConnectionState = useCallback((updates: Partial<ConnectionState>) => {
        setConnectionState(prev => ({ ...prev, ...updates }));
    }, []);

    // 오디오 처리 설정 함수
    const setupAudioProcessing = async (stream: MediaStream): Promise<boolean> => {
        try {
            // 오디오 컨텍스트 생성
            const context = new (window.AudioContext || (window as any).webkitAudioContext)({
                sampleRate: 16000,
            });

            // 오디오 컨텍스트 참조 저장
            audioContextRef.current = context;

            // 오디오 소스 노드 생성
            const source = context.createMediaStreamSource(stream);

            // AudioWorklet 모듈 로드
            await context.audioWorklet.addModule('/audioProcessor.js');

            // AudioWorkletNode 생성
            const workletNode = new AudioWorkletNode(context, 'audio-processor');

            // AudioWorkletNode 참조 저장
            workletNodeRef.current = workletNode;

            // 메시지 이벤트 핸들러 설정
            workletNode.port.onmessage = event => {
                if (event.data.type === 'init' || event.data.type === 'debug') {
                    return;
                }

                if (event.data.type === 'audioData' && event.data.buffer) {
                    try {
                        // AudioWorklet에서 받은 buffer는 이미 ArrayBuffer 형태
                        const buffer = event.data.buffer;

                        // Int16Array로 래핑하여 Uint8Array로 변환
                        const int16Array = new Int16Array(buffer);
                        const uint8Array = new Uint8Array(buffer);

                        // 너무 많은 로그가 발생하므로 4초에 한 번씩만 로그 출력
                        const now = Date.now();
                        if (
                            !lastAudioLogTimeRef.current ||
                            now - lastAudioLogTimeRef.current > 4000
                        ) {
                            lastAudioLogTimeRef.current = now;
                        }

                        // 오디오 데이터를 소켓을 통해 서버로 전송
                        if (buffer.byteLength > 0 && socket && socket.connected) {
                            try {
                                // audioData 이벤트로 오디오 데이터 전송
                                socket.emit('audioData', buffer);

                                // 로그 출력 (너무 많은 로그를 방지하기 위해 약 10초에 한 번씩만 출력)
                                if (
                                    !lastAudioSentLogTimeRef.current ||
                                    now - lastAudioSentLogTimeRef.current > 10000
                                ) {
                                    lastAudioSentLogTimeRef.current = now;
                                }
                            } catch (socketError) {
                                console.error('오디오 데이터 소켓 전송 오류:', socketError);
                            }
                        }
                    } catch (error) {
                        console.error('오디오 데이터 처리 중 오류:', error);
                    }
                }
            };

            // 오디오 그래프 연결
            source.connect(workletNode);
            workletNode.connect(context.destination);

            return true;
        } catch (error) {
            console.error('오디오 처리 설정 오류:', error);
            return false;
        }
    };

    // 강의 상태 토글 처리 함수 (시작 또는 종료)
    const handleClassToggle = async () => {
        try {
            setIsLoading(true);

            // 강의가 이미 시작된 경우 종료, 그렇지 않으면 시작
            if (isClassStarted || (classData && classData.status === 'IN_PROGRESS')) {
                // 번역이 활성화되어 있으면 먼저 번역 중지
                if (isTranslationActive) {
                    await handleTranslationToggle();
                }
                await handleClassEnd();
            } else {
                // 강의 시작 처리
                // 서버에 수업 시작 요청
                const response = await api.post(`/classes/${classId}/start`);

                // 소켓 설정만 처리 (번역 시작은 별도 버튼으로 수행)
                socket.emit('createClass', {
                    inputLanguage: classData?.inputLanguage,
                    classId: classData?.id,
                    className: classData?.title,
                    professorId: classData?.professorId,
                    professorLanguage: classData?.inputLanguage,
                    targetLanguages: classData?.targetLanguages,
                });

                // 강의 상태 업데이트
                setIsClassStarted(true);
                setClassData(prev => (prev ? { ...prev, status: 'IN_PROGRESS' } : null));

                alert('강의가 성공적으로 시작되었습니다.');
            }
        } catch (error) {
            console.error('수업 상태 변경 중 오류:', error);
            alert(`수업 상태 변경 중 오류가 발생했습니다: ${getErrorMessage(error)}`);
        } finally {
            setIsLoading(false);
        }
    };

    // 번역 시작/종료 토글 함수 추가
    const handleTranslationToggle = async () => {
        try {
            setIsLoading(true);

            if (isTranslationActive) {
                // 번역 종료 로직
                socket.emit('stopDeepgramSTT');

                // 오디오 스트림 종료
                if (audioStreamRef.current) {
                    audioStreamRef.current.getTracks().forEach(track => {
                        track.stop();
                    });
                    audioStreamRef.current = null;
                }

                // AudioWorkletNode 정리
                if (workletNodeRef.current) {
                    workletNodeRef.current.disconnect();
                    workletNodeRef.current = null;
                }

                // AudioContext 닫기
                if (audioContextRef.current) {
                    audioContextRef.current
                        .close()
                        .then(() => {
                            audioContextRef.current = null;
                        })
                        .catch(err => {
                            console.error('AudioContext 닫기 오류:', err);
                        });
                }

                // 연결 상태 업데이트
                updateConnectionState({
                    isDeepgramReady: false,
                    isStreamActive: false,
                });

                // 번역 상태 업데이트
                setIsTranslationActive(false);
            } else {
                // 번역 시작 로직
                try {
                    // 마이크 접근 권한 요청
                    const stream = await navigator.mediaDevices.getUserMedia({
                        audio: selectedMicrophoneId
                            ? { deviceId: { exact: selectedMicrophoneId } }
                            : true,
                    });

                    // 스트림 참조 저장
                    audioStreamRef.current = stream;

                    // 오디오 처리 설정
                    const success = await setupAudioProcessing(stream);
                    console.log('==============11');

                    if (success && isProfessor) {
                        console.log('==============2');
                        console.log(classData?.inputLanguage);
                        // Deepgram STT 시작 이벤트 발생
                        socket.emit('startDeepgramSTT', {
                            classId: classData?.id,
                            language: classData?.inputLanguage, // 기본 언어를 한국어로 설정
                            sampleRate: 16000, // 샘플 레이트를 16kHz로 변경
                        });

                        // 연결 상태 업데이트
                        updateConnectionState({
                            isDeepgramReady: true,
                            isStreamActive: true,
                        });

                        // 번역 상태 업데이트
                        setIsTranslationActive(true);
                    }
                } catch (error) {
                    console.error('번역 시작 중 오류 발생:', error);
                    alert('번역 시작 중 오류가 발생했습니다: ' + getErrorMessage(error));
                }
            }
        } catch (error) {
            console.error('번역 상태 변경 중 오류:', error);
            alert(`번역 상태 변경 중 오류가 발생했습니다: ${getErrorMessage(error)}`);
        } finally {
            setIsLoading(false);
        }
    };

    // 파일 선택 처리 - 소켓 기반 업로드로 수정
    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];

            // 파일 크기 제한 (30MB)
            if (file.size > 30 * 1024 * 1024) {
                alert('파일 크기는 30MB 이하여야 합니다.');
                return;
            }

            // PDF 파일만 허용
            if (!file.type.includes('pdf')) {
                alert('현재 PDF 파일만 업로드 가능합니다.');
                return;
            }

            try {
                setIsUploading(true);
                setUploadProgress(0);

                // FormData 생성
                const formData = new FormData();
                formData.append('file', file);

                // 진행 상태 표시를 위한 axios 설정
                const uploadConfig = {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    onUploadProgress: (progressEvent: any) => {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total,
                        );
                        setUploadProgress(percentCompleted);
                    },
                };

                // REST API를 통해 파일 업로드
                await api.post(`/classes/${classId}/pdf`, formData, uploadConfig);
            } catch (error) {
                console.error('파일 업로드 중 오류:', error);
                alert(`파일 업로드 중 오류가 발생했습니다: ${getErrorMessage(error)}`);
            } finally {
                setIsUploading(false);

                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }
        }
    };

    // 파일을 ArrayBuffer로 읽는 함수
    const readFileAsArrayBuffer = (file: File): Promise<ArrayBuffer> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                if (reader.result instanceof ArrayBuffer) {
                    resolve(reader.result);
                } else {
                    reject(new Error('파일을 ArrayBuffer로 변환할 수 없습니다.'));
                }
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsArrayBuffer(file);
        });
    };

    // PDF 선택 함수 - 목록에서 선택한 PDF 표시
    const handlePdfSelect = (pdf: PdfDoc) => {
        // PDF URL 설정하여 표시
        setDocumentViewerUrl(pdf.url);
        setFileUrl(pdf.url);
    };

    // 수업 종료 처리 함수
    const handleClassEnd = async () => {
        try {
            setIsLoading(true);

            // 소켓 이벤트로 클래스 종료 알림 (PDF 파일 정리 포함)
            if (socket && socket.connected) {
                socket.emit('endClass', { classId }, (response: any) => {
                    if (response && response.success) {
                    } else {
                        console.warn('강의 종료 소켓 이벤트 실패:', response?.message);
                    }
                });
            }

            const response = await api.post(`/classes/${classId}/end`);

            alert('강의가 성공적으로 종료되었습니다.');

            // 대시보드로 이동
            navigate('/dashboard');
        } catch (error) {
            console.error('수업 종료 중 오류:', error);
            alert(`수업 종료 중 오류가 발생했습니다: ${getErrorMessage(error)}`);
        } finally {
            setIsLoading(false);
        }
    };

    // 질문 제출 처리 함수
    const handleQuestionSubmit = async (question: string) => {
        try {
            // 로딩 표시
            setIsLoading(true);

            // 소켓 이벤트 발생 (실시간 전파)
            if (socket && socket.connected) {
                socket.emit(
                    'newQuestion',
                    {
                        classId,
                        studentId: userId,
                        studentName: userName,
                        text: question,
                    },
                    (response: any) => {
                        // 소켓 응답 처리 (선택적)
                        if (response && response.success) {
                            // 성공 메시지 표시
                            alert('질문이 성공적으로 제출되었습니다.');
                        } else if (response) {
                            console.error('질문 전송 실패:', response.message || '응답 없음');
                            alert(`질문 전송에 실패했습니다: ${response.message || '응답 없음'}`);
                        }
                    },
                );
            } else {
                throw new Error('소켓 연결이 활성화되지 않았습니다.');
            }
        } catch (error) {
            console.error('질문 제출 중 오류:', error);
            alert(`질문 제출 중 오류가 발생했습니다: ${getErrorMessage(error)}`);
        } finally {
            setIsLoading(false);
        }
    };

    // 질문 모달을 여는 함수
    const handleOpenQuestionsListModal = () => {
        setShowQuestionsListModal(true);
        // 모달을 열면 새 질문 표시를 초기화
        setHasNewQuestions(false);
    };

    // QR 코드 모달을 여는 함수
    const handleOpenQrModal = async () => {
        try {
            const currentUrl = window.location.href;
            const qrDataUrl = await QRCode.toDataURL(currentUrl, {
                width: 300,
                margin: 2,
            });
            setQrCodeUrl(qrDataUrl);
            setShowQrModal(true);
        } catch (error) {
            console.error('QR 코드 생성 오류:', error);
            alert('QR 코드 생성에 실패했습니다.');
        }
    };

    // 파일 선택 버튼 클릭 핸들러 추가
    const handleFileButtonClick = () => {
        fileInputRef.current?.click();
    };

    // 단일 언어 모드용 번역 패널 렌더링
    const renderTranslationsPanel = () => {
        // 표시할 콘텐츠 초기화
        const content: React.ReactNode[] = [];

        // 표시할 모든 항목을 추적하는 맵 (텍스트 기반 중복 방지)
        const textToItemMap = new Map();

        // 1. 먼저 모든 확정된 번역 결과를 맵에 추가
        translations.forEach(item => {
            if (item && item.sourceText) {
                const key = item.sourceText.trim().toLowerCase();
                textToItemMap.set(key, {
                    ...item,
                    isInterim: false,
                });
            }
        });

        // 2. 현재 인식 중인 텍스트가 맵에 없는 경우에만 추가
        if (currentTranscription && !currentTranscription.isFinal) {
            const key = currentTranscription.text.trim().toLowerCase();

            // 기존 번역 결과에 없거나 처리 중인 경우에만 임시 항목 추가
            const existingTranslation = translations.find(
                t => t.sourceText && t.sourceText.trim().toLowerCase() === key,
            );

            if (
                !existingTranslation &&
                !textToItemMap.has(key) &&
                currentTranscription.text.trim() !== ''
            ) {
                // 현재 음성 인식 중인 항목 추가 (노란색 박스)
                textToItemMap.set(key, {
                    id: currentTranscription.id,
                    sourceText: currentTranscription.text,
                    sourceLanguage: 'ko',
                    translations: currentTranscription.translations || {},
                    timestamp: new Date().getTime(),
                    isInterim: true, // 임시 항목 (노란색)
                });
            }
        }

        // 3. 맵의 값들을 배열로 변환하고 시간 순으로 정렬
        const uniqueItems = Array.from(textToItemMap.values()).sort((a, b) => {
            return a.timestamp - b.timestamp;
        });

        // 4. 각 항목 렌더링
        uniqueItems.forEach((item, index) => {
            const isInterim = item.isInterim === true;

            // 번역 결과가 있는지 확인
            let hasTranslation = false;
            let translatedText = '';

            if (item.translations) {
                if (Array.isArray(item.translations)) {
                    const matchingTranslation = item.translations.find(
                        (t: any) => t.language === selectedLanguage,
                    );
                    if (matchingTranslation && matchingTranslation.text) {
                        hasTranslation = true;
                        translatedText = matchingTranslation.text;
                    }
                } else if (typeof item.translations === 'object') {
                    const langText = item.translations[selectedLanguage];
                    if (langText) {
                        hasTranslation = true;
                        translatedText = langText;
                    }
                }
            }

            // 항목 렌더링
            content.push(
                <div
                    key={`item-${item.id || index}`}
                    style={{
                        ...translationStyles.translationItem,
                        borderLeft: isInterim ? '4px solid #FFC107' : '4px solid #4CAF50',
                        marginBottom: '1rem',
                    }}
                >
                    <p
                        style={{
                            ...translationStyles.originalText,
                            fontStyle: isInterim ? 'italic' : 'normal',
                            fontSize: `${fontSize}px`,
                        }}
                    >
                        {item.sourceText}
                        {isInterim ? ' ...' : ''}
                    </p>
                    {hasTranslation ? (
                        <p
                            style={{
                                ...translationStyles.translatedText,
                                fontStyle: isInterim ? 'italic' : 'normal',
                                fontSize: `${fontSize}px`,
                            }}
                        >
                            {translatedText}
                        </p>
                    ) : (
                        <p
                            style={{
                                ...translationStyles.translatedText,
                                color: '#888',
                                fontStyle: 'italic',
                                fontSize: `${fontSize}px`,
                            }}
                        >
                            번역 결과를 기다리는 중...
                        </p>
                    )}
                </div>,
            );
        });

        const language = findLanguageByCode(selectedLanguage);
        const languageName = language ? language.koreanName : '번역';

        // DOM 요소 참조 콜백 함수를 정의
        const setRef = (element: HTMLDivElement | null) => {
            if (element) {
                translationsPanelRef.current = element;

                // 새 요소가 생성된 후 스크롤을 하단으로 이동
                scrollToBottom(element);
            }
        };

        return (
            <div
                style={{
                    ...translationStyles.translationsPanel,
                    height: screenSize === 'mobile' ? 'auto' : '100%',
                    display: 'flex',
                    flexDirection: 'column' as const,
                }}
            >
                <div
                    style={{
                        flex: '0 0 auto',
                    }}
                >
                    <h3
                        style={{
                            margin: '0 0 15px 0',
                            fontSize: screenSize === 'mobile' ? '16px' : '18px',
                            lineHeight: screenSize === 'mobile' ? '1.4' : '1.2',
                        }}
                    >
                        번역 결과
                    </h3>
                    <div
                        className="translation-panel-header"
                        style={{
                            ...translationStyles.panelHeader,
                            marginTop: '0',
                            paddingTop: '0',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                gap: screenSize === 'mobile' ? '10px' : '5px',
                                alignItems: 'center',
                                flexDirection: 'row',
                            }}
                        >
                            {screenSize !== 'mobile' && (
                                <label
                                    style={{
                                        fontSize: '14px',
                                        marginRight: '5px',
                                        fontWeight: '500',
                                    }}
                                >
                                    글꼴 크기:
                                </label>
                            )}
                            <select
                                value={fontSize}
                                onChange={e => changeFontSize(parseInt(e.target.value))}
                                style={{
                                    padding: screenSize === 'mobile' ? '8px 12px' : '6px 10px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: screenSize === 'mobile' ? '8px' : '6px',
                                    fontSize: screenSize === 'mobile' ? '14px' : '14px',
                                    cursor: 'pointer',
                                    minWidth: screenSize === 'mobile' ? '80px' : '60px',
                                    minHeight: screenSize === 'mobile' ? '44px' : 'auto',
                                    backgroundColor: '#ffffff',
                                    color: '#374151',
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                    transition: 'all 0.15s ease-in-out',
                                    outline: 'none',
                                    fontWeight: '500',
                                }}
                                title="글꼴 크기 선택"
                                onMouseEnter={e => {
                                    const target = e.target as HTMLSelectElement;
                                    target.style.borderColor = '#9ca3af';
                                    target.style.boxShadow =
                                        '0 1px 3px rgba(0, 0, 0, 0.1), 0 0 0 3px rgba(99, 102, 241, 0.1)';
                                }}
                                onMouseLeave={e => {
                                    const target = e.target as HTMLSelectElement;
                                    target.style.borderColor = '#d1d5db';
                                    target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                                }}
                                onFocus={e => {
                                    const target = e.target as HTMLSelectElement;
                                    target.style.borderColor = '#6366f1';
                                    target.style.boxShadow =
                                        '0 1px 3px rgba(0, 0, 0, 0.1), 0 0 0 3px rgba(99, 102, 241, 0.1)';
                                }}
                                onBlur={e => {
                                    const target = e.target as HTMLSelectElement;
                                    target.style.borderColor = '#d1d5db';
                                    target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                                }}
                            >
                                {fontSizeOptions.map(size => (
                                    <option key={size} value={size}>
                                        {size}px
                                    </option>
                                ))}
                            </select>
                        </div>
                        <select
                            value={selectedLanguage}
                            onChange={e => setSelectedLanguage(e.target.value)}
                            style={translationStyles.languageSelector}
                            onMouseEnter={e => {
                                const target = e.target as HTMLSelectElement;
                                target.style.borderColor = '#9ca3af';
                                target.style.boxShadow =
                                    '0 1px 3px rgba(0, 0, 0, 0.1), 0 0 0 3px rgba(99, 102, 241, 0.1)';
                            }}
                            onMouseLeave={e => {
                                const target = e.target as HTMLSelectElement;
                                target.style.borderColor = '#d1d5db';
                                target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                            }}
                            onFocus={e => {
                                const target = e.target as HTMLSelectElement;
                                target.style.borderColor = '#6366f1';
                                target.style.boxShadow =
                                    '0 1px 3px rgba(0, 0, 0, 0.1), 0 0 0 3px rgba(99, 102, 241, 0.1)';
                            }}
                            onBlur={e => {
                                const target = e.target as HTMLSelectElement;
                                target.style.borderColor = '#d1d5db';
                                target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                            }}
                        >
                            {classData?.targetLanguages?.map(langCode => {
                                // 한국어는 제외
                                if (langCode === 'ko-KR') return null;

                                const language = findLanguageByCode(langCode);
                                return language ? (
                                    <option key={langCode} value={langCode}>
                                        {language.koreanName} ({language.nativeName})
                                    </option>
                                ) : null;
                            })}
                        </select>
                    </div>
                </div>
                <div ref={setRef} style={translationStyles.panelContent}>
                    {content.length > 0 ? (
                        content
                    ) : (
                        <div
                            style={{
                                textAlign: 'center',
                                color: '#888',
                                marginTop: '20px',
                                fontStyle: 'italic',
                            }}
                        >
                            번역 결과가 없습니다
                        </div>
                    )}
                    {/* 스크롤이 맨 아래로 가도록 빈 공간 추가 */}
                    <div style={{ height: '20px' }}></div>
                </div>
            </div>
        );
    };

    // 특정 언어에 대한 번역 패널 렌더링
    const renderTranslationsPanelForLanguage = (languageCode: string, languageName: string) => {
        // 한국어 패널은 제외
        if (languageCode === 'ko-KR') return null;

        // 표시할 콘텐츠 초기화
        const content: React.ReactNode[] = [];

        // 표시할 모든 항목을 추적하는 맵 (텍스트 기반 중복 방지)
        const textToItemMap = new Map();

        // 1. 먼저 모든 확정된 번역 결과를 맵에 추가
        translations.forEach(item => {
            if (item && item.sourceText) {
                const key = item.sourceText.trim().toLowerCase();
                textToItemMap.set(key, {
                    ...item,
                    isInterim: false,
                });
            }
        });

        // 2. 현재 인식 중인 텍스트가 맵에 없는 경우에만 추가
        if (currentTranscription && !currentTranscription.isFinal) {
            const key = currentTranscription.text.trim().toLowerCase();

            // 기존 번역 결과에 없거나 처리 중인 경우에만 임시 항목 추가
            const existingTranslation = translations.find(
                t => t.sourceText && t.sourceText.trim().toLowerCase() === key,
            );

            if (
                !existingTranslation &&
                !textToItemMap.has(key) &&
                currentTranscription.text.trim() !== ''
            ) {
                // 현재 음성 인식 중인 항목 추가 (노란색 박스)
                textToItemMap.set(key, {
                    id: currentTranscription.id,
                    sourceText: currentTranscription.text,
                    sourceLanguage: 'ko',
                    translations: currentTranscription.translations || {},
                    timestamp: new Date().getTime(),
                    isInterim: true, // 임시 항목 (노란색)
                });
            }
        }

        // 3. 맵의 값들을 배열로 변환하고 시간 순으로 정렬
        const uniqueItems = Array.from(textToItemMap.values()).sort((a, b) => {
            return a.timestamp - b.timestamp;
        });

        // 4. 각 항목 렌더링
        uniqueItems.forEach((item, index) => {
            const isInterim = item.isInterim === true;

            // 번역 결과가 있는지 확인
            let hasTranslation = false;
            let translatedText = '';

            if (item.translations) {
                if (Array.isArray(item.translations)) {
                    const matchingTranslation = item.translations.find(
                        (t: any) => t.language === languageCode,
                    );
                    if (matchingTranslation && matchingTranslation.text) {
                        hasTranslation = true;
                        translatedText = matchingTranslation.text;
                    }
                } else if (typeof item.translations === 'object') {
                    const langText = item.translations[languageCode];
                    if (langText) {
                        hasTranslation = true;
                        translatedText = langText;
                    }
                }
            }

            // 항목 렌더링
            content.push(
                <div
                    key={`item-${languageCode}-${item.id || index}`}
                    style={{
                        ...translationStyles.translationItem,
                        borderLeft: isInterim ? '4px solid #FFC107' : '4px solid #4CAF50',
                        marginBottom: '1rem',
                    }}
                >
                    <p
                        style={{
                            ...translationStyles.originalText,
                            fontStyle: isInterim ? 'italic' : 'normal',
                            fontSize: `${fontSize}px`,
                        }}
                    >
                        {item.sourceText}
                        {isInterim ? ' ...' : ''}
                    </p>
                    {hasTranslation ? (
                        <p
                            style={{
                                ...translationStyles.translatedText,
                                fontStyle: isInterim ? 'italic' : 'normal',
                                fontSize: `${fontSize}px`,
                            }}
                        >
                            {translatedText}
                        </p>
                    ) : (
                        <p
                            style={{
                                ...translationStyles.translatedText,
                                color: '#888',
                                fontStyle: 'italic',
                                fontSize: `${fontSize}px`,
                            }}
                        >
                            번역 결과를 기다리는 중...
                        </p>
                    )}
                </div>,
            );
        });

        // DOM 요소 참조 콜백 함수를 정의
        const setRef = (element: HTMLDivElement | null) => {
            if (element) {
                languageRefs.current[languageCode] = element;

                // 새 요소가 생성된 후 스크롤을 하단으로 이동
                scrollToBottom(element);
            }
        };

        // 패널 렌더링
        return (
            <div key={`panel-${languageCode}`} style={layoutStyles.gridPanel}>
                <div
                    style={{
                        flex: '0 0 auto',
                    }}
                >
                    <h3
                        style={{
                            margin: '0 0 15px 0',
                            fontSize: screenSize === 'mobile' ? '16px' : '18px',
                            lineHeight: screenSize === 'mobile' ? '1.4' : '1.2',
                        }}
                    >
                        번역 결과 ({languageName})
                    </h3>
                    <div
                        className="translation-panel-header"
                        style={{
                            ...translationStyles.panelHeader,
                            marginTop: '0',
                            paddingTop: '0',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                gap: screenSize === 'mobile' ? '8px' : '5px',
                                alignItems: 'center',
                                justifyContent: screenSize === 'mobile' ? 'flex-start' : 'center',
                            }}
                        >
                            <select
                                value={fontSize}
                                onChange={e => changeFontSize(parseInt(e.target.value))}
                                style={{
                                    padding: screenSize === 'mobile' ? '6px 10px' : '4px 8px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: screenSize === 'mobile' ? '8px' : '6px',
                                    fontSize: screenSize === 'mobile' ? '12px' : '12px',
                                    cursor: 'pointer',
                                    minWidth: screenSize === 'mobile' ? '70px' : '55px',
                                    minHeight: screenSize === 'mobile' ? '36px' : 'auto',
                                    backgroundColor: '#ffffff',
                                    color: '#374151',
                                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                    transition: 'all 0.15s ease-in-out',
                                    outline: 'none',
                                    fontWeight: '500',
                                }}
                                title="글꼴 크기 선택"
                                onMouseEnter={e => {
                                    const target = e.target as HTMLSelectElement;
                                    target.style.borderColor = '#9ca3af';
                                    target.style.boxShadow =
                                        '0 1px 2px rgba(0, 0, 0, 0.05), 0 0 0 2px rgba(99, 102, 241, 0.1)';
                                }}
                                onMouseLeave={e => {
                                    const target = e.target as HTMLSelectElement;
                                    target.style.borderColor = '#d1d5db';
                                    target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                                }}
                                onFocus={e => {
                                    const target = e.target as HTMLSelectElement;
                                    target.style.borderColor = '#6366f1';
                                    target.style.boxShadow =
                                        '0 1px 2px rgba(0, 0, 0, 0.05), 0 0 0 2px rgba(99, 102, 241, 0.1)';
                                }}
                                onBlur={e => {
                                    const target = e.target as HTMLSelectElement;
                                    target.style.borderColor = '#d1d5db';
                                    target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                                }}
                            >
                                {fontSizeOptions.map(size => (
                                    <option key={size} value={size}>
                                        {size}px
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
                <div
                    ref={setRef}
                    style={{
                        ...translationStyles.panelContent,
                        padding: '10px',
                    }}
                >
                    {content.length > 0 ? (
                        content
                    ) : (
                        <div
                            style={{
                                textAlign: 'center',
                                color: '#888',
                                marginTop: '20px',
                                fontStyle: 'italic',
                            }}
                        >
                            번역 결과가 없습니다
                        </div>
                    )}
                    {/* 스크롤이 맨 아래로 가도록 빈 공간 추가 */}
                    <div style={{ height: '20px' }}></div>
                </div>
            </div>
        );
    };

    const saveInputLanguageSettings = async (newInputLanguage: string) => {
        try {
            await api.put(`/classes/${classId}/input-language`, {
                inputLanguage: newInputLanguage,
            });

            if (classData) {
                setClassData({
                    ...classData,
                    inputLanguage: newInputLanguage,
                });
            }

            setInputLanguage(newInputLanguage);
            alert('입력 언어 설정이 저장되었습니다.');
        } catch (error) {}
    };

    // 번역 언어 설정 저장 함수
    const saveLanguageSettings = async (newTargetLanguages: string[]) => {
        try {
            // 최대 6개 언어만 선택 가능하도록 제한
            const limitedLanguages = newTargetLanguages.slice(0, 6);

            if (limitedLanguages.length !== newTargetLanguages.length) {
                console.warn(
                    `최대 6개 언어만 선택 가능합니다. ${newTargetLanguages.length}개에서 ${limitedLanguages.length}개로 제한됩니다.`,
                );
            }

            if (classId) {
                // 백엔드 API 호출
                await api.put(`/classes/${classId}/languages`, {
                    targetLanguages: limitedLanguages,
                });

                // 클래스 데이터 업데이트
                if (classData) {
                    setClassData({
                        ...classData,
                        targetLanguages: limitedLanguages,
                    });
                }

                // 상태 업데이트
                setTargetLanguages(limitedLanguages);

                // 알림 표시
                alert('번역 언어 설정이 저장되었습니다.');
            }
        } catch (error) {
            console.error('번역 언어 설정 저장 오류:', error);
            alert('번역 언어 설정 저장에 실패했습니다.');
        }
    };

    // 마이크 설정 저장 함수
    const saveMicrophoneSettings = (microphoneId: string) => {
        setSelectedMicrophoneId(microphoneId);
        setShowMicrophoneModal(false);

        // 로컬 스토리지에 선택한 마이크 저장
        localStorage.setItem('selectedMicrophoneId', microphoneId);
    };

    // 레이아웃 설정 저장 함수
    const saveLayoutSettings = (newShowClassMaterials: boolean, newShowAllLanguages: boolean) => {
        setShowClassMaterials(newShowClassMaterials);
        setShowAllLanguages(newShowAllLanguages);
        setShowLayoutModal(false);

        // 로컬 스토리지에 레이아웃 설정 저장
        localStorage.setItem('showClassMaterials', String(newShowClassMaterials));
        localStorage.setItem('showAllLanguages', String(newShowAllLanguages));
    };

    // TTS 설정 저장 함수
    const saveTtsSettings = (enabled: boolean) => {
        setTtsEnabled(enabled);
        setShowTtsModal(false);

        // TTS 비활성화 시 오디오 큐 클리어 및 처리된 번역 ID 초기화
        if (!enabled) {
            audioQueueManager.clear();
            setLastProcessedTranslationId(null);
        }
    };

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>강의실 정보를 불러오는 중입니다...</p>
            </div>
        );
    }

    if (error || !classData) {
        return (
            <div className="error-container">
                <h3>오류가 발생했습니다</h3>
                <p>{error}</p>
                <button onClick={() => navigate('/classes')}>강의실 목록으로 돌아가기</button>
            </div>
        );
    }

    return (
        <div className="classroom-container">
            {/* 상단 헤더 */}
            <header className="classroom-header">
                <div className="class-info">
                    <h1>{classData.title}</h1>
                    <div className="class-code" style={{ backgroundColor: 'white' }}>
                        <span>Code Number : {classData.id}</span>
                        <span> / </span>
                        <span>Password : {classData.password}</span>
                    </div>
                    <div className="class-subtitle">Code Number 와 Password 를 공유해주세요</div>
                </div>
                <div className="header-controls">
                    {isProfessor ? (
                        <>
                            <button
                                className={`btn-class-toggle ${
                                    isClassStarted || classData.status === 'IN_PROGRESS'
                                        ? 'btn-end-class'
                                        : 'btn-start-class'
                                }`}
                                onClick={handleClassToggle}
                                key={`toggle-button-${forceUpdate}`}
                                disabled={
                                    !(isClassStarted || classData.status === 'IN_PROGRESS') &&
                                    (!targetLanguages ||
                                        targetLanguages.filter(lang => lang !== 'ko-KR').length ===
                                            0)
                                }
                                title={
                                    !(isClassStarted || classData.status === 'IN_PROGRESS') &&
                                    (!targetLanguages ||
                                        targetLanguages.filter(lang => lang !== 'ko-KR').length ===
                                            0)
                                        ? '번역 언어를 먼저 선택해주세요'
                                        : ''
                                }
                            >
                                {isClassStarted || classData.status === 'IN_PROGRESS'
                                    ? '강의 종료'
                                    : '강의 시작'}
                            </button>

                            {/* 번역 언어 선택이 안 된 경우 안내 텍스트 추가 */}
                            {!(isClassStarted || classData.status === 'IN_PROGRESS') &&
                                (!targetLanguages ||
                                    targetLanguages.filter(lang => lang !== 'ko-KR').length ===
                                        0) && (
                                    <div
                                        className="language-warning"
                                        style={{
                                            color: '#e74c3c',
                                            fontSize: '0.8rem',
                                            marginLeft: '10px',
                                            alignSelf: 'center',
                                        }}
                                    >
                                        번역 언어를 먼저 선택해주세요
                                    </div>
                                )}

                            {/* 번역 시작/종료 버튼 추가 - 강의가 시작된 상태에서만 표시 */}
                            {(isClassStarted || classData.status === 'IN_PROGRESS') && (
                                <button
                                    className={`btn-class-toggle ${
                                        isTranslationActive ? 'btn-end-class' : 'btn-start-class'
                                    }`}
                                    onClick={handleTranslationToggle}
                                    style={{
                                        marginLeft: '10px',
                                    }}
                                >
                                    {isTranslationActive ? '번역 종료' : '번역 시작'}
                                </button>
                            )}
                        </>
                    ) : (
                        <button
                            className="btn-class-toggle btn-exit-class"
                            onClick={() => navigate('/dashboard')}
                        >
                            나가기
                        </button>
                    )}
                </div>
            </header>

            {/* 메인 콘텐츠 영역 */}
            <main className="classroom-main" style={layoutStyles.classroomMain}>
                <div className="classroom-content" style={layoutStyles.classroomContent}>
                    {/* 왼쪽 패널 - 파일 뷰어 수정 */}
                    {showClassMaterials && (
                        <div className="file-viewer-panel" style={layoutStyles.fileViewerPanel}>
                            <div className="panel-header" style={{ backgroundColor: 'white' }}>
                                <h2>강의 자료</h2>
                                {isProfessor && (
                                    <div className="file-controls">
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleFileSelect}
                                            accept=".pdf"
                                            style={{ display: 'none' }}
                                        />
                                        <button
                                            className="btn-file-select"
                                            onClick={handleFileButtonClick}
                                            disabled={isUploading}
                                        >
                                            {isUploading ? '업로드 중...' : '파일 선택'}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* PDF 목록 표시 영역 추가 */}
                            {pdfList.length > 0 && (
                                <div className="pdf-list-container">
                                    <h3 style={{ fontSize: '14px', margin: '0 0 8px 0' }}>
                                        PDF 목록
                                    </h3>
                                    <div className="pdf-list">
                                        {pdfList.map(pdf => (
                                            <div
                                                key={pdf.id}
                                                className="pdf-item"
                                                onClick={() => handlePdfSelect(pdf)}
                                                style={{
                                                    backgroundColor:
                                                        documentViewerUrl === pdf.url
                                                            ? '#e6f7ff'
                                                            : 'white',
                                                }}
                                            >
                                                <span style={{ marginRight: '5px' }}>📄</span>
                                                <span
                                                    style={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {pdf.filename}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* 업로드 진행 상태 표시 */}
                            {isUploading && (
                                <div className="upload-progress-container">
                                    <div className="upload-progress-bar">
                                        <div
                                            className="upload-progress-fill"
                                            style={{
                                                width: `${uploadProgress}%`,
                                            }}
                                        ></div>
                                    </div>
                                    <p className="upload-progress-text">
                                        업로드 중... {uploadProgress}%
                                    </p>
                                </div>
                            )}

                            <div className="file-container">
                                {fileUrl ? (
                                    <div className="file-preview">
                                        <iframe
                                            src={fileUrl}
                                            title="PDF Viewer"
                                            className="pdf-viewer"
                                        />
                                    </div>
                                ) : (
                                    <div className="file-placeholder">
                                        <div className="file-icon">📁</div>
                                        {isProfessor ? (
                                            <>
                                                <p>강의에 사용할 파일을 선택해주세요.</p>
                                                <p className="file-formats">
                                                    지원 형식: PDF만 지원
                                                </p>
                                                <button
                                                    className="btn-file-select-large"
                                                    onClick={handleFileButtonClick}
                                                    disabled={isUploading}
                                                >
                                                    {isUploading ? '업로드 중...' : '파일 선택'}
                                                </button>
                                            </>
                                        ) : (
                                            <p>아직 업로드된 강의 자료가 없습니다.</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* 번역 결과 섹션 */}
                    {!showAllLanguages ? (
                        // 단일 언어 모드 - 하나의 패널에 선택된 언어 표시
                        <div
                            className="translation-section"
                            style={layoutStyles.translationSection}
                        >
                            {renderTranslationsPanel()}
                        </div>
                    ) : (
                        // 모든 언어 동시 표시 모드 - 언어별로 패널 분리 (그리드 레이아웃)
                        <div className="translations-grid" style={layoutStyles.translationsGrid}>
                            {/* 선택된 언어별 패널만 표시 (한국어 제외) */}
                            {classData?.targetLanguages
                                ?.filter(langCode => langCode !== 'ko-KR')
                                .map(langCode => {
                                    const language = findLanguageByCode(langCode);
                                    if (!language) return null;

                                    return renderTranslationsPanelForLanguage(
                                        langCode,
                                        `${language.koreanName} (${language.nativeName})`,
                                    );
                                })}
                        </div>
                    )}
                </div>

                {/* 하단 컨트롤 영역 */}
                <div className="classroom-controls">
                    <div className="control-buttons">
                        {isProfessor ? (
                            // 교수용 컨트롤 버튼들
                            <>
                                {classData?.layout !== 'PRESENTATION' && (
                                    <button
                                        className="btn-control"
                                        onClick={handleOpenQuestionsListModal}
                                    >
                                        질문보기
                                        {hasNewQuestions && (
                                            <span className="notification-badge"></span>
                                        )}
                                    </button>
                                )}
                                {classData?.layout === 'PRESENTATION' && (
                                    <button className="btn-control" onClick={handleOpenQrModal}>
                                        QR 코드
                                    </button>
                                )}
                                <button
                                    className="btn-control"
                                    onClick={() => setShowInputLanguageModal(true)}
                                >
                                    입력 언어 설정
                                </button>
                                <button
                                    className="btn-control"
                                    onClick={() => setShowLanguageModal(true)}
                                >
                                    번역 언어 설정
                                </button>
                                <button
                                    className="btn-control"
                                    onClick={() => setShowMicrophoneModal(true)}
                                >
                                    마이크 설정
                                </button>
                                <button
                                    className="btn-control"
                                    onClick={() => setShowLayoutModal(true)}
                                >
                                    레이아웃 설정
                                </button>
                                <button
                                    className="btn-control"
                                    onClick={() => setShowHelpModal(true)}
                                >
                                    도움말
                                </button>
                            </>
                        ) : (
                            // 학생용 컨트롤 버튼들
                            <>
                                {classData?.layout !== 'PRESENTATION' && (
                                    <>
                                        <button
                                            className="btn-control"
                                            onClick={() => setShowQuestionModal(true)}
                                        >
                                            질문하기
                                        </button>
                                        <button
                                            className="btn-control"
                                            onClick={handleOpenQuestionsListModal}
                                        >
                                            질문보기
                                            {hasNewQuestions && (
                                                <span className="notification-badge"></span>
                                            )}
                                        </button>
                                    </>
                                )}
                                <button
                                    className="btn-control"
                                    onClick={() => setShowLayoutModal(true)}
                                >
                                    레이아웃 설정
                                </button>
                                <button
                                    className="btn-control"
                                    onClick={() => setShowTtsModal(true)}
                                >
                                    TTS 설정
                                </button>
                                <button
                                    className="btn-control"
                                    onClick={() => setShowHelpModal(true)}
                                >
                                    도움말
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </main>

            {/* 도움말 모달 */}
            {showHelpModal && (
                <div className="modal-overlay" onClick={() => setShowHelpModal(false)}>
                    <div className="modal-container" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{isProfessor ? '강의 시작 도움말' : '강의 참여 도움말'}</h3>
                            <button
                                className="modal-close-btn"
                                onClick={() => setShowHelpModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="help-content">
                                {isProfessor ? (
                                    // 교수용 도움말
                                    <>
                                        <h4>강의 시작 프로세스</h4>
                                        <ol className="help-steps">
                                            <li>
                                                <strong>마이크 설정</strong>
                                                <p>
                                                    마이크 설정 버튼을 클릭하여 사용할 마이크를
                                                    선택해주세요.
                                                </p>
                                            </li>
                                            <li>
                                                <strong>번역 언어 선택</strong>
                                                <p>
                                                    번역 언어 설정 버튼을 클릭하여 번역할 언어를
                                                    선택해주세요.
                                                </p>
                                            </li>
                                            <li>
                                                <strong>강의 자료 준비</strong>
                                                <p>
                                                    왼쪽 패널에서 강의에 사용할 파일을
                                                    업로드해주세요.
                                                </p>
                                            </li>
                                            <li>
                                                <strong>강의 시작</strong>
                                                <p>
                                                    상단의 '강의 시작' 버튼을 클릭하여 강의를
                                                    시작하세요.
                                                </p>
                                            </li>
                                            <li>
                                                <strong>번역 시작</strong>
                                                <p>
                                                    상단의 '번역 시작' 버튼을 클릭하여 번역을
                                                    시작하세요.
                                                </p>
                                            </li>
                                            <li>
                                                <strong>실시간 번역 확인</strong>
                                                <p>
                                                    오른쪽 패널에서 실시간으로 번역 결과를 확인할 수
                                                    있습니다.
                                                </p>
                                            </li>
                                            <li>
                                                <strong>강의 종료</strong>
                                                <p>
                                                    강의가 끝나면 상단의 '강의 종료' 버튼을 클릭하여
                                                    종료하세요.
                                                </p>
                                            </li>
                                        </ol>
                                    </>
                                ) : (
                                    // 학생용 도움말
                                    <>
                                        <h4>강의 참여 안내</h4>
                                        <ol className="help-steps">
                                            <li>
                                                <strong>번역 결과 확인</strong>
                                                <p>
                                                    교수님의 강의 내용이 실시간으로 번역되어 오른쪽
                                                    패널에 표시됩니다.
                                                </p>
                                            </li>
                                            <li>
                                                <strong>레이아웃 설정</strong>
                                                <p>
                                                    레이아웃 설정 버튼을 클릭하여 원하는 화면
                                                    구성으로 변경할 수 있습니다.
                                                </p>
                                            </li>
                                            <li>
                                                <strong>질문하기</strong>
                                                <p>
                                                    질문이 있으면 하단의 '질문하기' 버튼을 클릭하여
                                                    질문을 작성할 수 있습니다.
                                                </p>
                                            </li>
                                            <li>
                                                <strong>강의 자료 확인</strong>
                                                <p>
                                                    왼쪽 패널에서 교수님이 업로드한 강의 자료를
                                                    확인할 수 있습니다.
                                                </p>
                                            </li>
                                            <li>
                                                <strong>강의 종료</strong>
                                                <p>
                                                    강의가 끝나면 상단의 '나가기' 버튼을 클릭하여
                                                    대시보드로 돌아갈 수 있습니다.
                                                </p>
                                            </li>
                                        </ol>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-primary" onClick={() => setShowHelpModal(false)}>
                                확인
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <InputLanguageSettingsModal
                isOpen={showInputLanguageModal}
                onClose={() => setShowInputLanguageModal(false)}
                inputLanguage={inputLanguage}
                onSave={saveInputLanguageSettings}
            />

            {/* 언어 설정 모달 */}
            <LanguageSettingsModal
                isOpen={showLanguageModal}
                onClose={() => setShowLanguageModal(false)}
                targetLanguages={targetLanguages}
                onSave={saveLanguageSettings}
            />

            {/* 마이크 설정 모달 */}
            <MicrophoneSettingsModal
                isOpen={showMicrophoneModal}
                onClose={() => setShowMicrophoneModal(false)}
                selectedMicrophoneId={selectedMicrophoneId}
                onSave={saveMicrophoneSettings}
            />

            {/* 레이아웃 설정 모달 */}
            <LayoutSettingsModal
                isOpen={showLayoutModal}
                onClose={() => setShowLayoutModal(false)}
                showClassMaterials={showClassMaterials}
                showAllLanguages={showAllLanguages}
                onSave={saveLayoutSettings}
            />

            {/* TTS 설정 모달 */}
            <TtsSettingsModal
                isOpen={showTtsModal}
                onClose={() => setShowTtsModal(false)}
                ttsEnabled={ttsEnabled}
                onSave={saveTtsSettings}
            />

            {/* 질문하기 모달 */}
            <QuestionModal
                isOpen={showQuestionModal}
                onClose={() => setShowQuestionModal(false)}
                onSubmit={handleQuestionSubmit}
            />

            {/* 질문 목록 모달 */}
            <QuestionsListModal
                isOpen={showQuestionsListModal}
                onClose={() => setShowQuestionsListModal(false)}
                classId={classId || ''}
                isProfessor={isProfessor}
                socket={socket}
            />

            {/* QR 코드 모달 */}
            {showQrModal && (
                <div className="modal-overlay" onClick={() => setShowQrModal(false)}>
                    <div className="modal-container" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>강의실 QR 코드</h3>
                            <button
                                className="modal-close-btn"
                                onClick={() => setShowQrModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div
                            className="modal-body"
                            style={{ textAlign: 'center', padding: '20px' }}
                        >
                            {qrCodeUrl && (
                                <img
                                    src={qrCodeUrl}
                                    alt="강의실 QR 코드"
                                    style={{
                                        maxWidth: '100%',
                                        height: 'auto',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        padding: '10px',
                                        backgroundColor: 'white',
                                    }}
                                />
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn-primary" onClick={() => setShowQrModal(false)}>
                                확인
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClassRoom;
